import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";

import { ORDER_ITEMS_LIST_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { formatMoney } from "../../utils/currency";
import BackButton from "../../common/back-button";
import AppTable from "../../common/app-table";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";

export default function OrderItemsList({ refreshKey, onNavigate }) {
  const storedParams = localStorage.getItem("activePageParams");
  const params = storedParams ? JSON.parse(storedParams) : {};
  const orderId = params.orderId;

  const { getValidToken } = useAuth();

  const [orderCurrency, setOrderCurrency] = useState(params.currency || "");
  const [rowCount, setRowCount] = useState(0);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  const [orderItems, setOrderItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search_query: searchQuery,
    });

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(
        `${ORDER_ITEMS_LIST_ENDPOINT}${orderId}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      const currencyFromApi =
        jsonData.currency ??
        jsonData.results?.[0]?.currency ??
        params.currency ??
        "";
      if (currencyFromApi) {
        setOrderCurrency(currencyFromApi);
      }

      setOrderItems(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [controller.page, controller.rowsPerPage, refreshKey, searchQuery]);

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const handleBackToOrders = () => {
    onNavigate?.("orders");
  };

  const tableColumns = [
    {
      id: "order_item_id",
      label: "Item ID",
      align: "left",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.order_item_id}
        </Typography>
      ),
    },
    {
      id: "product_id",
      label: "Product ID",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.product_id || "N/A"}
        </Typography>
      ),
    },
    {
      id: "product_name",
      label: "Product Name",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.product_name || "N/A"}
        </Typography>
      ),
    },
    {
      id: "price",
      label: "Price",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {formatMoney(row.price, row.currency ?? orderCurrency)}
        </Typography>
      ),
    },
    {
      id: "quantity",
      label: "Quantity",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.quantity || "N/A"}
        </Typography>
      ),
    },
    {
      id: "currency",
      label: "Currency",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {(row.currency ?? orderCurrency) || "N/A"}
        </Typography>
      ),
    },
    {
      id: "subtotal",
      label: "Subtotal",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {formatMoney(row.subtotal, row.currency ?? orderCurrency)}
        </Typography>
      ),
    },
    {
      id: "total",
      label: "Total",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {formatMoney(row.total, row.currency ?? orderCurrency)}
        </Typography>
      ),
    },
    {
      id: "updated_at",
      label: "Updated At",
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {formatWithTzLabel(row.updated_at_iso, "DD-MM-YYYY hh:mm A")}
        </Typography>
      ),
    },
  ];

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC",
    },
    "& td": {
      borderBottom: "none",
      borderTop: "none",
    },
  });

  return (
    <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
      <Box sx={{ mb: 3 }}>
        <BackButton onClick={handleBackToOrders} />
      </Box>
      <AppTable
        columns={tableColumns}
        rows={orderItems}
        rowKey={(row, index) => index}
        maxHeight={660}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        rowSx={rowSx}
        paperSx={{
          border: "1px solid #DCDFE5",
          borderRadius: "12px",
          mb: 2,
        }}
        emptyMessage="No order items found"
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
